import { useState } from 'react';
import { ViewerModalFrame, ViewerModalSection, type ViewerModalTheme } from './ViewerModalFrame';

interface ShareModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  shareLink: string;
  copiedLabel?: string;
  copyLabel?: string;
  downloadUrl?: string | null;
  downloadFilename?: string;
  downloadLabel?: string;
  theme?: ViewerModalTheme;
}

export function ShareModal({
  open,
  onClose,
  title = 'Share 3D Manual',
  shareLink,
  copiedLabel = 'Copied!',
  copyLabel = 'Copy',
  downloadUrl = null,
  downloadFilename = 'manual.pdf',
  downloadLabel = 'Download PDF',
  theme,
}: ShareModalProps) {
  const [copied, setCopied] = useState(false);

  const t = {
    primaryTextColor: 'rgba(255, 255, 255, 0.95)',
    secondaryTextColor: 'rgba(255, 255, 255, 0.7)',
    accentColor: 'rgb(56, 189, 248)',
    borderColor: 'rgba(255, 255, 255, 0.1)',
    ...theme,
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const el = document.createElement('textarea');
      el.value = shareLink;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleDownload = async () => {
    if (!downloadUrl) return;
    try {
      const response = await fetch(downloadUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = downloadFilename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('[ShareModal] Download failed:', err);
    }
  };

  return (
    <ViewerModalFrame open={open} onClose={onClose} title={title} titleIcon="share" theme={theme}>
      {/* Share link */}
      <ViewerModalSection title="Share this link" theme={theme}>
        <div style={{ display: 'flex' }}>
          <input
            type="text"
            value={shareLink}
            readOnly
            onClick={(e) => (e.target as HTMLInputElement).select()}
            style={{
              flex: 1,
              padding: '0.65rem 0.85rem',
              border: `1px solid ${t.borderColor}`,
              borderRight: 'none',
              borderRadius: '0.75rem 0 0 0.75rem',
              background: 'rgba(255, 255, 255, 0.04)',
              color: t.primaryTextColor,
              fontSize: '0.75rem',
              outline: 'none',
              fontFamily: 'inherit',
            }}
          />
          <button
            onClick={() => void handleCopy()}
            style={{
              background: t.accentColor,
              color: '#0f1219',
              padding: '0.65rem 0.85rem',
              border: 'none',
              borderRadius: '0 0.75rem 0.75rem 0',
              fontSize: '0.75rem',
              fontWeight: 600,
              cursor: 'pointer',
              minWidth: '4.5rem',
            }}
          >
            {copied ? copiedLabel : copyLabel}
          </button>
        </div>
      </ViewerModalSection>

      {/* Download */}
      {downloadUrl && (
        <ViewerModalSection title="Download" theme={theme}>
          <button
            onClick={() => void handleDownload()}
            style={{
              width: '100%',
              padding: '0.85rem 1rem',
              borderRadius: '0.75rem',
              background: 'rgba(255, 255, 255, 0.06)',
              border: `1px solid ${t.borderColor}`,
              color: t.primaryTextColor,
              fontSize: '0.85rem',
              fontWeight: 500,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              transition: 'all 0.3s',
            }}
          >
            <span className="material-symbols-outlined" style={{ fontSize: '1.1rem', fontWeight: 300 }}>
              download
            </span>
            {downloadLabel}
          </button>
        </ViewerModalSection>
      )}
    </ViewerModalFrame>
  );
}
